import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useMemo } from 'react'

import { useDocumentMarketDataIdState } from '@pages/ProductRegistration/LegalDocuments/ReadDocumentMarketDataPage'

import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSignup } from '@context/SignupContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { LegalDocumentDto } from '@model/CompanyLegalDocumentDto'

import { useProducts } from '../../../hooks/useProducts'
import { FormValuesLegalDocs } from '../PersonalDetailsDocumentsFactory'
import { PersonalDetailsStep6Form } from './PersonalDetailsStep6Form'

interface OuterProps {
  shouldReceiveNewsletter?: boolean
  onSubmit: (values: FormValuesLegalDocs) => Promise<void>
  reversed?: boolean
}

export const PersonalDetailsStep6Page: React.FC<OuterProps> = (props) => {
  const { onSubmit } = props
  const { signupData } = useSignup()
  const { marketDataClassificationId } = useDocumentMarketDataIdState()
  const { selectedProductIds } = useProducts()
  const locale = useSessionLanguage()

  const apiClient = useApiClient(ClientApiClient)
  const tickmillCompanyId = signupData.lead?.tickmillCompany.id
  const countryId = signupData.lead?.country?.id
  const leadProductIds = useMemo(
    () => signupData.lead?.products?.map((product) => product.id) || [],
    [signupData.lead?.products]
  )
  const productIds = useMemo(
    () => (selectedProductIds.length > 0 ? selectedProductIds : leadProductIds),
    [leadProductIds, selectedProductIds]
  )
  const isLeadReady = !!signupData.lead && !!tickmillCompanyId && !!countryId

  const documentsQuery = useQuery({
    queryKey: queryKeys.signup.legalDocuments(locale, tickmillCompanyId, productIds, countryId),
    queryFn: () =>
      apiClient.getLegalDocumentsQuery(locale, tickmillCompanyId, productIds, countryId),
    enabled: isLeadReady,
  })
  const documents = documentsQuery.data || []
  const isLoading = !isLeadReady || (documentsQuery.isFetching && !documentsQuery.data)

  return (
    <PersonalDetailsStep6Form
      {...props}
      onSubmit={onSubmit}
      lead={signupData.lead}
      marketDataClassificationId={marketDataClassificationId}
      groupReferences={groupReferences(documents)}
      products={productIds}
      isLoading={isLoading}
      documents={documents
        .filter(filterDocumentsByClient)
        .filter(filterDuplicateDocuments)
        .sort((a, b) => a.order - b.order)}
    />
  )
}

const groupReferences = (documents: LegalDocumentDto[] = []): (string | null)[] => {
  const groupReferences = documents.map((x) => x.groupReference)

  return groupReferences.filter(function (item, pos) {
    return groupReferences.indexOf(item) === pos
  })
}

export const filterDocumentsByClient = (document: LegalDocumentDto): boolean => {
  return document.step === 'client'
}

export const filterDuplicateDocuments = (
  value: LegalDocumentDto,
  index: number,
  self: LegalDocumentDto[]
): boolean => index === self.findIndex((t) => t.id === value.id)
